<h2 class="bd-title">Behavior</h2>

@if (successMessage) {
<alert type="success" [dismissible]="true" (onClose)="successMessage = undefined">
  <coso-alert-message>
    <div class="d-inline" [innerHTML]="successMessage"></div>
  </coso-alert-message>
</alert>
} @if (errorMessage) {
<alert type="danger" [dismissible]="true" (onClose)="errorMessage = undefined">
  <coso-alert-message>
    <div class="d-inline" [innerHTML]="errorMessage"></div>
  </coso-alert-message>
</alert>
}

<form #settingsForm="ngForm" id="settingsForm">
  @if (settings$ | async; as settings) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="replicateStateCodeLabel" class="cs-larger-text cs-bolder-text">Replicate record StateCode</div>
      <div id="replicateStateCodeSubLabel" class="cs-secondary-text">
        The status (StateCode) of a record in Dynamics 365 will be applied to secured SharePoint objects. When the status (StateCode) of a record is set to Inactive (Closed, Won, Lost, Cancelled,
        etc.), the SharePoint object will be read-only.
      </div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="replicateStateCode"
        name="replicateStateCode"
        aria-labelledby="replicateStateCodeLabel"
        aria-describedby="replicateStateCodeSubLabel"
        [attr.aria-checked]="settings.replicateStateCode"
        [(ngModel)]="settings.replicateStateCode"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="replicateStateCode">{{ settings.replicateStateCode ? 'On' : 'Off' }}</label>
    </div>
  </div>
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="replicateUserAccessModeLabel" class="cs-larger-text cs-bolder-text">Replicate user access mode</div>
      <div id="replicateUserAccessModeSubLabel" class="cs-secondary-text">
        Set whether Dynamics 365 user access mode will be replicated.
        <ul>
          <li>Read-write access mode: Read and write access to SharePoint location</li>
          <li>Read access mode: Read access to SharePoint location</li>
          <li>Administrative, Support User, Non-Interactive: No access to SharePoint location</li>
        </ul>
      </div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="replicateUserAccessMode"
        name="replicateUserAccessMode"
        aria-labelledby="replicateUserAccessModeLabel"
        aria-describedby="replicateUserAccessModeSubLabel"
        [attr.aria-checked]="settings.replicateUserAccessMode"
        [(ngModel)]="settings.replicateUserAccessMode"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="replicateUserAccessMode">{{ settings.replicateUserAccessMode ? 'On' : 'Off' }}</label>
    </div>
  </div>
  @if (settings$ | async; as settings) { @if (settings.groupStorage === GroupStorage.SharePoint) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <label id="automaticSharePointUserCreationLabel" for="automaticSharePointUserCreation" class="cs-larger-text cs-bolder-text">Automatic SharePoint user creation</label>
      <div id="automaticSharePointUserCreationSubLabel" class="cs-secondary-text">
        Automatically creates a SharePoint user for Dynamics 365 user that is not present in SharePoint.
        <ul>
          <li>Disabled: No SharePoint user will be created</li>
          <li>All: All Dynamics 365 enabled users will be created in SharePoint</li>
          <li>On Demand: Dynamics 365 enabled users that are supposed to have access to SharePoint documents will be created in SharePoint</li>
        </ul>
      </div>
    </div>
    <div class="cs-input-wrapper">
      <select
        class="form-control"
        id="automaticSharePointUserCreation"
        name="automaticSharePointUserCreation"
        aria-describedby="automaticSharePointUserCreationLabel"
        [(ngModel)]="settings.automaticSharePointUserCreation"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      >
        @for (mode of automaticSharePointUserCreationModes; track mode) {
        <option [ngValue]="AutomaticSharePointUserCreationMode[mode]">{{ getAutomaticSharePointUserCreationModeName(mode) }}</option>
        }
      </select>
    </div>
  </div>
  } }
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="skipDisabledLocationsLabel" class="cs-larger-text cs-bolder-text">Skip disabled document locations</div>
      <div id="skipDisabledLocationsSubLabel" class="cs-secondary-text">
        SharePoint document locations that are inactive (or disabled), or are using relativeurl and its parent location is inactive (or disabled), will be skipped/ignored. The permissions will not be
        replicated for these document locations.
      </div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="skipDisabledLocations"
        name="skipDisabledLocations"
        aria-labelledby="skipDisabledLocationsLabel"
        aria-describedby="skipDisabledLocationsSubLabel"
        [attr.aria-checked]="settings.skipDisabledLocations"
        [(ngModel)]="settings.skipDisabledLocations"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="skipDisabledLocations">{{ settings.skipDisabledLocations ? 'On' : 'Off' }}</label>
    </div>
  </div>
  @if (isSaaS === false) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="backgroundPermissionsVerificationLabel" class="cs-larger-text cs-bolder-text">Background permission verification</div>
      <div id="backgroundPermissionsVerificationSubLabel" class="cs-secondary-text">
        Secured SharePoint objects will be verified when the permissions are externally changed. When security misalignment is detected, it will automatically fix security of SharePoint object. Be
        aware that by enabling this option, there is the danger of generating a constant load at your SharePoint server(s).
      </div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="backgroundPermissionsVerification"
        name="backgroundPermissionsVerification"
        aria-labelledby="backgroundPermissionsVerificationLabel"
        aria-describedby="backgroundPermissionsVerificationSubLabel"
        [attr.aria-checked]="settings.backgroundPermissionsVerification"
        [(ngModel)]="settings.backgroundPermissionsVerification"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="backgroundPermissionsVerification">{{ settings.backgroundPermissionsVerification ? 'On' : 'Off' }}</label>
    </div>
  </div>
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="lateGroupCreateEnabledLabel" class="cs-larger-text cs-bolder-text">Late group creation</div>
      <div id="lateGroupCreateEnabledSubLabel" class="cs-secondary-text">Set whether groups will be created immediately or when needed</div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="lateGroupCreateEnabled"
        name="lateGroupCreateEnabled"
        aria-labelledby="lateGroupCreateEnabledLabel"
        aria-describedby="lateGroupCreateEnabledSubLabel"
        [attr.aria-checked]="settings.lateGroupCreateEnabled"
        [(ngModel)]="settings.lateGroupCreateEnabled"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="lateGroupCreateEnabled">{{ settings.lateGroupCreateEnabled ? 'On' : 'Off' }}</label>
    </div>
  </div>
  @if (settings.groupStorage === GroupStorage.SharePoint || settings.groupStorage === GroupStorage.Office365) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="groupCachingLabel" class="cs-larger-text cs-bolder-text">SharePoint group local caching</div>
      <div id="groupCachingSubLabel" class="cs-secondary-text">The SharePoint groups and memberships will be cached in the local database, improving the speed of the full iteration.</div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="groupCaching"
        name="groupCaching"
        aria-labelledby="groupCachingLabel"
        aria-describedby="groupCachingSubLabel"
        [attr.aria-checked]="settings.groupCaching"
        [(ngModel)]="settings.groupCaching"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="groupCaching">{{ settings.groupCaching ? 'On' : 'Off' }}</label>
    </div>
  </div>
  }
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="omitEmptyGroupsLabel" class="cs-larger-text cs-bolder-text">Omit empty groups</div>
      <div id="omitEmptyGroupsSubLabel" class="cs-secondary-text">Enable to create only groups containing users. Empty will be skipped or deleted.</div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="omitEmptyGroups"
        name="omitEmptyGroups"
        aria-labelledby="omitEmptyGroupsLabel"
        aria-describedby="omitEmptyGroupsSubLabel"
        [attr.aria-checked]="settings.omitEmptyGroups"
        [(ngModel)]="settings.omitEmptyGroups"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="omitEmptyGroups">{{ settings.omitEmptyGroups ? 'On' : 'Off' }}</label>
    </div>
  </div>
  }
  @if (settings.groupStorage === GroupStorage.SharePoint) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="orphanedSharePointGroupCleanupLabel" class="cs-larger-text cs-bolder-text">Delete orphaned SharePoint groups</div>
      <div id="orphanedSharePointGroupCleanupSubLabel" class="cs-secondary-text">If enabled, orphaned SharePoint groups that were created by the service will be removed from SharePoint.</div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="orphanedSharePointGroupCleanup"
        name="orphanedSharePointGroupCleanup"
        aria-labelledby="orphanedSharePointGroupCleanupLabel"
        aria-describedby="orphanedSharePointGroupCleanupSubLabel"
        [attr.aria-checked]="settings.orphanedSharePointGroupCleanup"
        [(ngModel)]="settings.orphanedSharePointGroupCleanup"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="orphanedSharePointGroupCleanup">{{ settings.orphanedSharePointGroupCleanup ? 'On' : 'Off' }}</label>
    </div>
  </div>
  }
  @if (settings.groupStorage === GroupStorage.AzureActiveDirectory || settings.groupStorage === GroupStorage.ActiveDirectory) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="orphanedDirectoryGroupCleanupLabel" class="cs-larger-text cs-bolder-text">
        {{ settings.groupStorage === GroupStorage.ActiveDirectory ? 'Delete orphaned Active Directory groups' : 'Delete orphaned Entra ID groups' }}
      </div>
      <div id="orphanedDirectoryGroupCleanupSubLabel" class="cs-secondary-text">
        {{ settings.groupStorage === GroupStorage.ActiveDirectory
          ? 'If enabled, orphaned Active Directory groups that were created by the service will be removed from Active Directory.'
          : 'If enabled, orphaned Entra ID groups that were created by the service will be removed from Entra ID.' }}
      </div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="orphanedDirectoryGroupCleanup"
        name="orphanedDirectoryGroupCleanup"
        aria-labelledby="orphanedDirectoryGroupCleanupLabel"
        aria-describedby="orphanedDirectoryGroupCleanupSubLabel"
        [attr.aria-checked]="settings.orphanedDirectoryGroupCleanup"
        [(ngModel)]="settings.orphanedDirectoryGroupCleanup"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="orphanedDirectoryGroupCleanup">{{ settings.orphanedDirectoryGroupCleanup ? 'On' : 'Off' }}</label>
    </div>
  </div>
  }
  @if (settings.groupStorage === GroupStorage.AzureActiveDirectory) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="orphanedSharePointDirectoryGroupCleanupLabel" class="cs-larger-text cs-bolder-text">Delete orphaned SharePoint Entra ID group principals</div>
      <div id="orphanedSharePointDirectoryGroupCleanupSubLabel" class="cs-secondary-text">If enabled, orphaned Entra ID directory group principals at SharePoint that were created by the service will be removed from SharePoint site user info list.</div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="orphanedSharePointDirectoryGroupCleanup"
        name="orphanedSharePointDirectoryGroupCleanup"
        aria-labelledby="orphanedSharePointDirectoryGroupCleanupLabel"
        aria-describedby="orphanedSharePointDirectoryGroupCleanupSubLabel"
        [attr.aria-checked]="settings.orphanedSharePointDirectoryGroupCleanup"
        [(ngModel)]="settings.orphanedSharePointDirectoryGroupCleanup"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="orphanedSharePointDirectoryGroupCleanup">{{ settings.orphanedSharePointDirectoryGroupCleanup ? 'On' : 'Off' }}</label>
    </div>
  </div>
  }
  @if (isSaaS === false) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="diffPermissionWriteModeLabel" class="cs-larger-text cs-bolder-text">Diff permission write mode</div>
      <div id="diffPermissionWriteModeSubLabel" class="cs-secondary-text">Only permission changes will be applied. The whole permission set for the folder is not guaranteed.</div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="diffPermissionWriteMode"
        name="diffPermissionWriteMode"
        aria-labelledby="diffPermissionWriteModeLabel"
        aria-describedby="diffPermissionWriteModeSubLabel"
        [attr.aria-checked]="settings.diffPermissionWriteMode"
        [(ngModel)]="settings.diffPermissionWriteMode"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="diffPermissionWriteMode">{{ settings.diffPermissionWriteMode ? 'On' : 'Off' }}</label>
    </div>
  </div>
  }
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="replicateAccessTeamTemplateAsGroupLabel" class="cs-larger-text cs-bolder-text">Access team templates as groups</div>
      <div id="replicateAccessTeamTemplateAsGroupSubLabel" class="cs-secondary-text">
        Set whether Dynamics 365 access team templates (record access teams) will be replicated as groups. Otherwise, permissions will be granted per user.
      </div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="replicateAccessTeamTemplateAsGroup"
        name="replicateAccessTeamTemplateAsGroup"
        aria-labelledby="replicateAccessTeamTemplateAsGroupLabel"
        aria-describedby="replicateAccessTeamTemplateAsGroupSubLabel"
        [attr.aria-checked]="settings.replicateAccessTeamTemplateAsGroup"
        [(ngModel)]="settings.replicateAccessTeamTemplateAsGroup"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="replicateAccessTeamTemplateAsGroup">{{ settings.replicateAccessTeamTemplateAsGroup ? 'On' : 'Off' }}</label>
    </div>
  </div>
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="replicateAccessTeamAsGroupLabel" class="cs-larger-text cs-bolder-text">Access teams as groups</div>
      <div id="replicateAccessTeamAsGroupSubLabel" class="cs-secondary-text">
        Set whether Dynamics 365 access teams will be replicated as groups. Otherwise, permissions will be granted per user.
      </div>
    </div>
    <div class="custom-control custom-switch">
      <input
        role="switch"
        type="checkbox"
        class="custom-control-input"
        id="replicateAccessTeamAsGroup"
        name="replicateAccessTeamAsGroup"
        aria-labelledby="replicateAccessTeamAsGroupLabel"
        aria-describedby="replicateAccessTeamAsGroupSubLabel"
        [attr.aria-checked]="settings.replicateAccessTeamAsGroup"
        [(ngModel)]="settings.replicateAccessTeamAsGroup"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <label class="custom-control-label" for="replicateAccessTeamAsGroup">{{ settings.replicateAccessTeamAsGroup ? 'On' : 'Off' }}</label>
    </div>
  </div>
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <div id="skipBrokenFolderReferencesLabel" class="cs-larger-text cs-bolder-text">Skip broken links</div>
      <div id="skipBrokenFolderReferencesSubLabel" class="cs-secondary-text">
        When a SharePoint document location is identified as a broken link, it will be remembered in the local database. There will be no more attempts to write permissions for this SharePoint
        document location.
      </div>
    </div>
    <div class="d-lg-flex justify-content-lg-between w-25">
      <button
        type="button"
        id="clearBrokenLinkCacheButton"
        class="btn btn-danger btn-sm mr-2"
        data-toggle="tooltip"
        data-placement="top"
        data-original-title="Clear broken link cache from local database"
        (click)="clearBrokenLinkCache()"
        [disabled]="readonly$ | async"
      >
        <span class="fa fa-eraser mr-1" aria-hidden="true"></span>Clear cache
      </button>
      <div class="custom-control custom-switch">
        <input
          role="switch"
          type="checkbox"
          class="custom-control-input"
          id="skipBrokenFolderReferences"
          name="skipBrokenFolderReferences"
          aria-labelledby="skipBrokenFolderReferencesLabel"
          aria-describedby="skipBrokenFolderReferencesLabel"
          [attr.aria-checked]="settings.skipBrokenFolderReferences"
          [(ngModel)]="settings.skipBrokenFolderReferences"
          (ngModelChange)="updateSettings(settings)"
          [disabled]="readonly$ | async"
        />
        <label class="custom-control-label" for="skipBrokenFolderReferences">{{ settings.skipBrokenFolderReferences ? 'On' : 'Off' }}</label>
      </div>
    </div>
  </div>
  @if (isSaaS === false) {
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <label id="permissionEvaluateWorkersLabel" for="permissionEvaluateWorkers" class="cs-larger-text cs-bolder-text">Evaluation workers</label>
      <div id="permissionEvaluateWorkersSubLabel" class="cs-secondary-text">
        Set the number of parallel worker threads to evaluate permissions for SharePoint document locations based on the current Dynamics 365 security model. A higher number means higher performance,
        but also higher resources usage.
      </div>
    </div>
    <div class="cs-input-wrapper">
      <input
        type="number"
        class="form-control"
        id="permissionEvaluateWorkers"
        name="permissionEvaluateWorkers"
        #permissionEvaluateWorkers="ngModel"
        min="1"
        max="99"
        [class.is-invalid]="permissionEvaluateWorkers.touched && permissionEvaluateWorkers.invalid"
        cosoRangeValidator
        [attr.aria-invalid]="permissionEvaluateWorkers.invalid"
        [attr.aria-describedby]="'permissionEvaluateWorkersSubLabel' + (permissionEvaluateWorkers.touched && permissionEvaluateWorkers.invalid ? ' permissionEvaluateWorkers-errors' : '')"
        [(ngModel)]="settings.permissionEvaluateWorkers"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      />
      <span class="fa fa-times invalid-feedback-icon" aria-hidden="true"></span>
      @if (permissionEvaluateWorkers.touched && permissionEvaluateWorkers.invalid) { @if (permissionEvaluateWorkers.errors?.min) {
      <div class="invalid-feedback" id="permissionEvaluateWorkers-errors" role="alert">
        The Evaluation workers field must be greater than or equal to 1.
      </div>
      } @if (permissionEvaluateWorkers.errors?.max) {
      <div class="invalid-feedback" id="permissionEvaluateWorkers-errors" role="alert">
        The Evaluation workers field must be less than or equal to 99.
      </div>
      } }
    </div>
  </div>
  }
  <div class="cs-row">
    <div class="cs-label-wrapper">
      <label id="multipleLinkResolutionTypeLabel" for="multipleLinkResolutionType" class="cs-larger-text cs-bolder-text">Multiple link resolution type</label>
      <div id="multipleLinkResolutionTypeSubLabel" class="cs-secondary-text">
        Select the permission resolution type in case of multiple document locations pointing to same SharePoint object.
        <ul>
          <li>None: no handling for this case</li>
          <li>Oldest Wins: the permissions for oldest document location will be set</li>
          <li>Newest Wins: the permissions for newest document location will be set</li>
          <li>Logical And: the logical conjunction of all related permissions will be set</li>
          <li>Logical Or: the logic disjunction of all related permissions will be set</li>
        </ul>
      </div>
    </div>
    <div class="cs-input-wrapper">
      <select
        class="form-control"
        id="multipleLinkResolutionType"
        name="multipleLinkResolutionType"
        aria-describedby="multipleLinkResolutionTypeSubLabel"
        [(ngModel)]="settings.multipleLinkResolutionType"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      >
        @for (type of multipleLinkResolutionTypes; track type) {
        <option [ngValue]="MultipleLinkResolutionType[type]">{{ getMultipleLinkResolutionTypeName(type) }}</option>
        }
      </select>
    </div>
  </div>

    <div class="cs-row">
    <div class="cs-label-wrapper">
      <label id="sdkMessageProcessingStepsRegistrationModeLabel" for="sdkMessageProcessingStepsRegistrationMode" class="cs-larger-text cs-bolder-text">
        Plugin steps registration mode
      </label>
      <div id="sdkMessageProcessingStepsRegistrationModeSubLabel" class="cs-secondary-text">
        Defines the technique how dynamic plugin steps will be registered at your Dynamics 365.
        <ul>
          <li>Unmanaged customizations: The dynamically registered plugin steps are created as unmanaged customizations. This is the default behavior, but it does not work when unmanaged customizations are blocked at target environment.</li>
          <li>Managed solution: The plugin steps will be packed into managed solution with unique name 'Coso_PermissionsReplicator_DynamicSteps'. This is suitable for environments where unmanaged customizations are blocked.</li>
        </ul>
      </div>
    </div>      
    <div class="cs-input-wrapper">
      <select
        class="form-control"
        id="sdkMessageProcessingStepsRegistrationMode"
        name="sdkMessageProcessingStepsRegistrationMode"
        aria-describedby="sdkMessageProcessingStepsRegistrationModeLabel"
        [(ngModel)]="settings.sdkMessageProcessingStepsRegistrationMode"
        (ngModelChange)="updateSettings(settings)"
        [disabled]="readonly$ | async"
      >
        @for (mode of sdkMessageProcessingStepsRegistrationModes; track mode) {
          <option [ngValue]="SdkMessageProcessingStepsRegistrationMode[mode]">{{ getSdkMessageProcessingStepsRegistrationModeName(mode) }}</option>
        }
      </select>
    </div>
  </div>
  }
</form>
